/**
 * Logger service for PhishPhind application
 * Provides standardized logging with different levels and module context
 */

export enum LogLevel {
  DEBUG = 'debug',
  INFO = 'info',
  WARN = 'warn',
  ERROR = 'error',
}

interface LogOptions {
  module?: string;
  timestamp?: boolean;
  persist?: boolean;
  data?: unknown;
  message?: string;
}

const DEFAULT_OPTIONS: LogOptions = {
  module: 'app',
  timestamp: true,
  persist: false,
};

const MAX_LOGS = 1000;
const STORAGE_KEY = 'phishphind_logs';

// Color styles for different log levels (console only)
const LOG_STYLES = {
  [LogLevel.DEBUG]: 'color: #6b7280',
  [LogLevel.INFO]: 'color: #3b82f6',
  [LogLevel.WARN]: 'color: #f59e0b',
  [LogLevel.ERROR]: 'color: #ef4444; font-weight: bold',
};

class Logger {
  private logs: string[] = [];
  private enabled = true;
  private logLevel: LogLevel = LogLevel.INFO;

  constructor() {
    this.loadLogsFromStorage();
  }

  /**
   * Configure logger settings
   */
  configure(options: { enabled?: boolean; level?: LogLevel }) {
    if (options.enabled !== undefined) {
      this.enabled = options.enabled;
    }
    if (options.level !== undefined) {
      this.logLevel = options.level;
    }
  }

  /**
   * Debug level logging
   */
  debug(message: string, options?: LogOptions) {
    this.log(LogLevel.DEBUG, message, options);
  }

  /**
   * Info level logging
   */
  info(message: string, options?: LogOptions) {
    this.log(LogLevel.INFO, message, options);
  }

  /**
   * Warning level logging
   */
  warn(message: string, options?: LogOptions) {
    this.log(LogLevel.WARN, message, options);
  }

  /**
   * Error level logging
   */
  error(message: string, options?: LogOptions) {
    this.log(LogLevel.ERROR, message, options);
  }

  /**
   * Get all logs
   */
  getLogs(): string[] {
    return [...this.logs];
  }

  /**
   * Clear all logs
   */
  clearLogs() {
    this.logs = [];
    localStorage.removeItem(STORAGE_KEY);
  }

  /**
   * Internal logging implementation
   */
  private log(level: LogLevel, message: string, options?: LogOptions) {
    if (!this.enabled || !this.shouldLog(level)) {
      return;
    }

    const mergedOptions = { ...DEFAULT_OPTIONS, ...options };
    const { module, timestamp, persist, data } = mergedOptions;

    const timestampStr = timestamp ? `[${new Date().toISOString()}] ` : '';
    const moduleStr = module ? `[${module}] ` : '';
    const logMessage = `${timestampStr}${level.toUpperCase()} ${moduleStr}${message}`;

    // Console output with color
    if (level === LogLevel.ERROR) {
      console.error(`%c${logMessage}`, LOG_STYLES[level], data || '');
    } else if (level === LogLevel.WARN) {
      console.warn(`%c${logMessage}`, LOG_STYLES[level], data || '');
    } else if (level === LogLevel.INFO) {
      console.info(`%c${logMessage}`, LOG_STYLES[level], data || '');
    } else {
      console.debug(`%c${logMessage}`, LOG_STYLES[level], data || '');
    }

    // Store log if persistence is enabled
    if (persist) {
      this.logs.push(logMessage);
      this.trimLogs();
      this.saveLogsToStorage();
    }
  }

  /**
   * Check if log level should be output based on current settings
   */
  private shouldLog(level: LogLevel): boolean {
    const levels = [LogLevel.DEBUG, LogLevel.INFO, LogLevel.WARN, LogLevel.ERROR];
    const currentIndex = levels.indexOf(this.logLevel);
    const messageIndex = levels.indexOf(level);
    return messageIndex >= currentIndex;
  }

  /**
   * Trim logs to maximum size
   */
  private trimLogs() {
    if (this.logs.length > MAX_LOGS) {
      this.logs = this.logs.slice(-MAX_LOGS);
    }
  }

  /**
   * Save logs to local storage
   */
  private saveLogsToStorage() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.logs));
    } catch (error) {
      console.error('Failed to save logs to storage', error);
    }
  }

  /**
   * Load logs from local storage
   */
  private loadLogsFromStorage() {
    try {
      const storedLogs = localStorage.getItem(STORAGE_KEY);
      if (storedLogs) {
        this.logs = JSON.parse(storedLogs);
      }
    } catch (error) {
      console.error('Failed to load logs from storage', error);
    }
  }
}

// Export a singleton instance
export const logger = new Logger();

// Configure logger based on environment
if (import.meta.env.MODE === 'development') {
  logger.configure({ level: LogLevel.DEBUG });
} else {
  logger.configure({ level: LogLevel.INFO });
}

export default logger;
